import axios, { AxiosError } from 'axios'

import { getSessionStorage, setSessionStorage } from '@utils/states/sessionStorage'

import { useAuthentication } from '@pages/Authentication/context'
import { toast } from '@utils/functions/notificationToast'

const API_URL = import.meta.env.VITE_AUTH_API_URL

export const api = axios.create({ baseURL: API_URL })

let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

const addSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback)
}

api.interceptors.request.use(
  (config) => {
    const token = getSessionStorage('@SMARTOPS_ACCESSTOKEN') as string

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error?.response?.status === 401 && !originalRequest._retry) {
      const { signOut } = useAuthentication.getState()

      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise((resolve) => {
          addSubscriber((newToken) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            resolve(api(originalRequest))
          })
        })
      }

      isRefreshing = true

      try {
        const refreshToken = getSessionStorage<string>('@SMARTOPS_REFRESHTOKEN')

        if (!refreshToken) throw new Error('Refresh token não encontrado')

        const res = await axios.post(`${API_URL}/api/auth/refresh`, { data: { refreshToken } })

        setSessionStorage('@SMARTOPS_ACCESSTOKEN', res.data.tokens.accessToken)
        setSessionStorage('@SMARTOPS_REFRESHTOKEN', res.data.tokens.refreshToken)
        setSessionStorage('@SMARTOPS_USER_DATA', {
          user: res.data.user,
          cls: res.data.filiais,
          competencePeriod: res.data.periodosCompetencia
        })

        api.defaults.headers.common['Authorization'] = `Bearer ${res.data.tokens.accessToken}`

        isRefreshing = false
        onRefreshed(res.data.tokens.accessToken)

        originalRequest.headers['Authorization'] = `Bearer ${res.data.tokens.accessToken}`

        return api(originalRequest)
      } catch (error) {
        toast.error('Sessão expirada - redirecionando para login', (error as AxiosError)?.status, 5000)

        setTimeout(() => {
          signOut()
        }, 5000)

        isRefreshing = false
        return Promise.reject(error)
      }
    }

    // RF09/F04: tratamento de Acesso Negado (403)
    if (error?.response?.status === 403) {
      toast.error('Acesso negado. Você não possui permissão para esta ação.', (error as AxiosError)?.status)
    }

    return Promise.reject(error)
  }
)
